import type { LogWithRelations } from '@owl/shared';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { formatDateTime, timeAgo } from '../../../lib/time';
import { Icon } from '../../icons';
import { MessageTypeChip, copyToClipboard } from '../../shared';
import { BottomSheet } from '../BottomSheet';
import type { MobileShellCtx } from '../MobileShellCtx';

const LABEL: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
  fontWeight: 500,
  marginBottom: 8,
};

function highlightJson(value: unknown): ReactNode {
  const json = JSON.stringify(value, null, 2);
  if (!json) return null;
  const parts: ReactNode[] = [];
  const regex =
    /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(true|false)|(null)/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null = regex.exec(json);
  while (m !== null) {
    if (m.index > last) parts.push(json.slice(last, m.index));
    const cls = m[1]
      ? 'json-key'
      : m[2]
        ? 'json-str'
        : m[3]
          ? 'json-num'
          : m[4]
            ? 'json-bool'
            : 'json-null';
    parts.push(
      <span key={key++} className={cls}>
        {m[0]}
      </span>,
    );
    last = regex.lastIndex;
    m = regex.exec(json);
  }
  if (last < json.length) parts.push(json.slice(last));
  return parts;
}

export function LogDetailSheet({ ctx, logId }: { ctx: MobileShellCtx; logId: string }) {
  const [log, setLog] = useState<LogWithRelations | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getLog(logId);
        if (!cancelled) setLog(r.log);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logId]);

  const hasMetadata = log?.metadata && Object.keys(log.metadata).length > 0;

  return (
    <BottomSheet
      title="Log"
      onClose={ctx.closeSheet}
      action={
        hasMetadata ? (
          <button
            type="button"
            className="sheet-close"
            aria-label="Copy metadata"
            title={copied ? 'Copiado' : 'Copiar JSON'}
            onClick={() =>
              copyToClipboard(JSON.stringify(log?.metadata, null, 2), () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              })
            }
          >
            {copied ? <Icon.Check size={13} /> : <Icon.Copy size={13} />}
          </button>
        ) : undefined
      }
    >
      {err && <div className="m-error-banner">{err}</div>}
      {!log && !err && <div className="m-loading">Cargando…</div>}
      {log && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <MessageTypeChip mt={log.messageType} />
            <span
              className="mono"
              style={{ fontSize: 11, color: 'var(--fg-4)', marginLeft: 'auto' }}
            >
              {timeAgo(log.timestamp)}
            </span>
          </div>

          <div
            style={{
              background: 'var(--bg-2)',
              border: '1px solid var(--border-1)',
              borderRadius: 12,
              padding: 14,
              fontSize: 14,
              lineHeight: 1.5,
              marginBottom: 14,
              wordBreak: 'break-word',
            }}
          >
            {log.message}
          </div>

          <div style={LABEL}>Contexto</div>
          <div className="kv-block" style={{ marginBottom: 14 }}>
            <div className="kv-row">
              <span className="k">App</span>
              <span className="v">{log.application.name}</span>
            </div>
            <div className="kv-row">
              <span className="k">Origen</span>
              <span className="v">
                {log.application.product}/{log.application.company}
                {log.application.instance ? `/${log.application.instance}` : ''}
              </span>
            </div>
            <div className="kv-row">
              <span className="k">Timestamp</span>
              <span className="v">{formatDateTime(log.timestamp)}</span>
            </div>
            <div className="kv-row">
              <span className="k">ID</span>
              <span className="v">{log.id}</span>
            </div>
          </div>

          {hasMetadata && (
            <>
              <div style={LABEL}>Metadata</div>
              <pre className="json-block">{highlightJson(log.metadata)}</pre>
            </>
          )}
        </>
      )}
    </BottomSheet>
  );
}
